import { useEffect, useState } from "react";
import api, { apiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tags, Upload, Store, EyeOff } from "lucide-react";
import { Modal, ConfirmDialog } from "@/components/panel/ui";
import { Badge, Button, DataTable, Field, Input, PageHeader } from "@/components/ui-kit";

const EMPTY = { name: "", image: "", store_visible: true, free_shipping: true, free_shipping_min: "" };

function CategoryForm({ category, onClose, onSaved }) {
  const [form, setForm] = useState(category
    ? { name: category.name, image: category.image || "", store_visible: category.store_visible !== false,
        free_shipping: category.free_shipping !== false,
        free_shipping_min: category.free_shipping_min ?? "" }
    : EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome da categoria"); return; }
    setSaving(true);
    try {
      /* Campo em branco vira NULL, não string vazia: nulo significa "herda o
         mínimo geral", e "" chegaria ao banco como zero — dando frete grátis a
         qualquer valor, que é o oposto do que quem limpou o campo quis. */
      const payload = { ...form, free_shipping_min: form.free_shipping_min === "" ? null : Number(form.free_shipping_min) };
      if (category?.slug) await api.patch(`/admin/categories/${category.slug}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(category ? "Categoria atualizada" : "Categoria criada");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={category ? "Editar categoria" : "Nova categoria"} testId="category-form-dialog">
      <div className="space-y-3">
        <Field label="Nome" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-form-name" />
        </Field>
        <div className="flex items-center gap-4">
          {form.image && <img src={imgUrl(form.image)} alt="" className="w-16 h-16 rounded-md object-cover border border-slate-200" />}
          <label className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 border border-brand-200 hover:bg-brand-50 rounded-lg px-3 py-2 cursor-pointer">
            <Upload size={16} /> {uploading ? "Enviando..." : "Enviar imagem"}
            {/* Texto do rótulo é condicional, então o nome acessível vem explícito. */}
            <input type="file" accept="image/*" onChange={upload} aria-label="Escolher imagem da categoria"
              className="hidden" data-testid="category-form-upload" />
          </label>
        </div>
        {/* A categoria some da LOJA, não do sistema. O texto diz as duas coisas
            porque "desativar" costuma ser lido como "sumir de tudo", e aqui o
            balcão continua vendendo normalmente — é justamente para isso que a
            opção existe. */}
        <label className="flex items-start gap-3 border border-slate-200 rounded-lg p-3 cursor-pointer hover:bg-slate-50">
          {/* O rótulo visível é o <span> abaixo, que muda de texto conforme o
              estado — daí o nome acessível vir explícito aqui. */}
          <input type="checkbox" checked={form.store_visible}
            onChange={(e) => setForm({ ...form, store_visible: e.target.checked })}
            data-testid="category-form-store-visible" aria-label="Mostrar na loja virtual"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600" />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-900">Mostrar na loja virtual</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              {form.store_visible
                ? "A categoria e os produtos dela aparecem para quem navega no site."
                : "A categoria e os produtos dela ficam fora do site. Continuam no estoque, no PDV e nos relatórios."}
            </span>
          </span>
        </label>

        {/* FRETE GRÁTIS — o que motivou isto: cadeira de rodas é cara e
            volumosa de enviar, e não pode entrar no mesmo benefício da
            munhequeira que cabe num envelope. */}
        <label className="flex items-start gap-3 border border-slate-200 rounded-lg p-3 cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={form.free_shipping}
            onChange={(e) => setForm({ ...form, free_shipping: e.target.checked })}
            data-testid="category-form-free-shipping" aria-label="Participa do frete grátis"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600" />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-900">Participa do frete grátis</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              {form.free_shipping
                ? "Produtos desta categoria podem ganhar frete grátis ao atingir o mínimo."
                : "Nunca tem frete grátis — e basta UM item desta categoria no carrinho para o pedido inteiro pagar frete."}
            </span>
          </span>
        </label>

        {form.free_shipping && (
          <Field label="Mínimo próprio para frete grátis (R$)"
            hint="Em branco usa o mínimo geral das Configurações. Com várias categorias no carrinho, vale o MAIOR mínimo entre elas.">
            <Input type="number" min="0" step="0.01" value={form.free_shipping_min}
              onChange={(e) => setForm({ ...form, free_shipping_min: e.target.value })}
              placeholder="usar o geral" data-testid="category-form-free-shipping-min" mono />
          </Field>
        )}

        <Button onClick={save} loading={saving} size="lg" block data-testid="category-form-save">
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </Modal>
  );
}

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setCategories(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    try { await api.delete(`/admin/categories/${c.slug}`); toast.success("Categoria excluída"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-categories-page">
      <PageHeader
        eyebrow="Operação"
        title="Categorias"
        icon={Tags}
        description="Como o catálogo é organizado na loja. A categoria só pode ser excluída quando não tem produto vinculado."
        actions={<Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="category-new-btn">Nova categoria</Button>}
      />

      <DataTable
        caption="Categorias do catálogo"
        rows={categories}
        rowKey={(c) => c.slug}
        empty={{
          icon: Tags,
          title: "Nenhuma categoria",
          desc: "Crie categorias para organizar seus produtos — elas viram os filtros da loja.",
          action: <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>Criar categoria</Button>,
          testId: "category-empty",
        }}
        columns={[
          {
            key: "categoria", header: "Categoria",
            cell: (c) => (
              <div className="flex items-center gap-3">
                {c.image
                  ? <img src={imgUrl(c.image)} alt="" className="w-9 h-9 rounded object-cover border border-slate-200" />
                  : <div className="w-9 h-9 rounded bg-slate-100" />}
                <span className="font-medium text-slate-900">{c.name}</span>
              </div>
            ),
          },
          { key: "slug", header: "Slug", mono: true, className: "text-xs text-slate-400", cell: (c) => c.slug },
          { key: "produtos", header: "Produtos", align: "right", mono: true, cell: (c) => c.products_count },
          {
            // Estado de vitrine na própria linha: escondida é a exceção, e quem
            // abre a lista precisa ver quais são sem entrar em cada uma.
            key: "loja", header: "Loja virtual", align: "center",
            cell: (c) => (c.store_visible === false
              ? <Badge tone="neutral"><EyeOff size={12} aria-hidden /> Escondida</Badge>
              : <Badge tone="success"><Store size={12} aria-hidden /> Na loja</Badge>),
          },
          {
            key: "acoes", header: "Ações", align: "right", className: "whitespace-nowrap",
            cell: (c) => (
              <>
                <Button variant="ghost" size="iconSm" onClick={() => { setEditing(c); setShowForm(true); }}
                  data-testid="category-edit-btn" aria-label={`Editar ${c.name}`} className="hover:text-brand-600"><Pencil size={16} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => setDeleting(c)}
                  data-testid="category-delete-btn" aria-label={`Excluir ${c.name}`} className="hover:text-red-600"><Trash2 size={16} /></Button>
              </>
            ),
          },
        ]}
      />

      {showForm && <CategoryForm category={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <ConfirmDialog open={!!deleting} onClose={() => setDeleting(null)} onConfirm={() => remove(deleting)}
        title="Excluir categoria" message={`Excluir a categoria "${deleting?.name}"? Só é possível se não houver produtos vinculados.`} confirmLabel="Excluir" />
    </div>
  );
}
